import env from "@/env";
import { useClusterStore } from "@/store/cluster";
import { AuthMachineContext } from "@/store/auth";
import { getClusterHourCount } from "@/store/simpleCluster";
import { useUserStore } from "@/store/user";
import {
  ClusterForm,
  ClusterType,
  CreateClusterData,
  DynamicSupplyHardware,
  SimpleClusterForm
} from "@/types";
import { objectWithkeys } from "@/utils/array";
import { isPaygCluster } from "@/utils/payg";
import {
  getPersistedClusterState,
  getPersistedExpiration,
  hasClusterDraftSession,
  PERSISTED_FIELDS,
  persistClusterDraft
} from "@/utils/cluster";
import { useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { VerticalFlowAnalytics } from "../analytics";
import { useQueryParams } from "@/hooks/useQueryParams";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { scrollVerticalFlowToTop } from "@/utils/scroll";
import { invalidFieldKeysFromFormikErrors } from "@/utils/formikErrors";
import { SavedDeploymentConfig } from "@/types/savedDeploymentConfig";
import {
  applySavedPayload,
  matchSavedHardware,
  MatchSavedHardwareResult
} from "@/utils/savedDeploymentConfig";

function shouldInitiallyLoadClusterData(persistedStorageKey: string): boolean {
  if (typeof window === "undefined") return false;
  const hardwareId = new URLSearchParams(window.location.search).get("hardware_id");
  const saved = getPersistedClusterState(persistedStorageKey);
  const canRestore = Boolean(saved) && hasClusterDraftSession(persistedStorageKey);
  if (hardwareId && !canRestore) return true;
  if (env.FEATUREFLAG_DELAYED_LOGIN && canRestore) return true;
  return false;
}

type UseVerticalFlowFormLogicOptions<T extends "processor" | "customize" | string> = {
  analytics: Pick<
    VerticalFlowAnalytics,
    | "trackCompletedStep"
    | "trackImageSelected"
    | "trackProcessorSelected"
    | "trackSshKeyAdded"
    | "trackSummaryEvents"
    | "trackIntermediateStepCompleted"
  >;
  persistedStorageKey: string;
  processorStepTitle: string;
  defaultStepState: Record<T, "current" | "next" | "completed">;
  getNextStep: (currentStep: T, stepState: Record<T, "current" | "next" | "completed">) => T;
  getPreviousStep: (currentStep: T, stepState: Record<T, "current" | "next" | "completed">) => T;
  fetchHardwareWithFilters: () => Promise<{ hardware: DynamicSupplyHardware[] }>;
  onRetrievePersistedState: (
    persistedState: Record<string, unknown>
  ) => Record<T, "current" | "next" | "completed">;
  isSubmitPossible: (
    currentStep: T,
    values: SimpleClusterForm,
    errors: Record<string, unknown>
  ) => boolean;
  isIntermediateStep: (currentStep: T) => boolean;
  /** When false, featured `hardware_id` links pre-select hardware but do not advance the flow (e.g. recipes-first containers). */
  advanceStepOnFeaturedHardwareDeepLink?: boolean;
  /** When false, defer featured `hardware_id` handling until config is known (e.g. async recipes-first flag). */
  featuredHardwareDeepLinkConfigReady?: boolean;
};

export const useVerticalFlowFormLogic = <
  GenericFormStepType extends "processor" | "customize" | string
>(
  formData: unknown,
  isLg: boolean,
  {
    defaultStepState,
    analytics,
    persistedStorageKey,
    processorStepTitle = "Select Your Processor",
    fetchHardwareWithFilters,
    isSubmitPossible,
    onRetrievePersistedState,
    getNextStep,
    getPreviousStep,
    isIntermediateStep,
    advanceStepOnFeaturedHardwareDeepLink = true,
    featuredHardwareDeepLinkConfigReady = true
  }: UseVerticalFlowFormLogicOptions<GenericFormStepType>
) => {
  const {
    trackCompletedStep,
    trackProcessorSelected,
    trackSummaryEvents,
    trackIntermediateStepCompleted
  } = analytics;
  const { values, errors, setFieldValue, setValues, validateForm, setTouched } =
    useFormikContext<SimpleClusterForm>();

  // PAYG has no rental window: pin the (hidden) duration fields to one hour so every
  // price quote and cost display means "per hour", including drafts restored with a
  // longer duration persisted before PAYG.
  const isPayg = isPaygCluster(values.clusterType);
  useEffect(() => {
    if (!isPayg) return;
    if (values.durationType !== "hourly") setFieldValue("durationType", "hourly");
    if (values.durationQuantity !== 1) setFieldValue("durationQuantity", 1);
  }, [isPayg, values.durationType, values.durationQuantity, setFieldValue]);

  const [loadingClusterData, setLoadingClusterData] = useState(() =>
    shouldInitiallyLoadClusterData(persistedStorageKey)
  );
  const [selectedHardware, setSelectedHardware] = useState<DynamicSupplyHardware | null>(null);
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [stepState, setStepState] =
    useState<Record<GenericFormStepType, "current" | "next" | "completed">>(defaultStepState);
  const [hasUserSpecifiedName, setHasUserSpecifiedName] = useState(false);

  /** When defaultStepState updates after mount (e.g. async feature flag), apply it only if stepState still matches the pre-update default — avoids clobbering persistence or user navigation. */
  const initialDefaultStepSerializedRef = useRef<string | null>(null);
  useEffect(() => {
    const serialized = JSON.stringify(defaultStepState);
    if (initialDefaultStepSerializedRef.current === null) {
      initialDefaultStepSerializedRef.current = serialized;
      return;
    }
    if (serialized === initialDefaultStepSerializedRef.current) {
      return;
    }
    const prevDefaultSerialized = initialDefaultStepSerializedRef.current;
    initialDefaultStepSerializedRef.current = serialized;
    setStepState((prev) =>
      JSON.stringify(prev) === prevDefaultSerialized ? defaultStepState : prev
    );
  }, [defaultStepState]);

  const setRequiresLoginModalOpen = useUserStore((state) => state.setRequiresLoginModalOpen);
  const setRequiresLoginMode = useUserStore((state) => state.setRequiresLoginMode);
  const sendDripEvent = useUserStore((state) => state.sendDripEvent);
  const isLoggedIn = AuthMachineContext.useSelector((state) => state.context.isLoggedIn);
  const getClusterOptions = useClusterStore((state) => state.getClusterOptions);
  const queryParams = useQueryParams<{
    hardware_id?: string;
  }>();

  const currentStep: GenericFormStepType = useMemo(() => {
    for (const step in stepState) {
      if (stepState[step as GenericFormStepType] === "current") return step as GenericFormStepType;
    }
    return Object.keys(defaultStepState)[0] as GenericFormStepType;
  }, [stepState, defaultStepState]);

  const isInitialStepRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialStepRenderRef.current) {
      isInitialStepRenderRef.current = false;
      return;
    }
    window.requestAnimationFrame(() => {
      scrollVerticalFlowToTop();
    });
  }, [currentStep]);

  const isSubmitDisabled = useMemo(() => {
    return (
      !values.name ||
      !values.hardware ||
      !values.hardwareQuantity ||
      !isSubmitPossible(currentStep as GenericFormStepType, values, errors)
    );
  }, [values, isSubmitPossible, currentStep, errors]);

  const clusterOptions = useMemo(() => {
    return getClusterOptions(values as ClusterForm, formData as CreateClusterData);
  }, [values, formData, getClusterOptions]);

  const computedValues = useMemo(() => {
    const hardwareList =
      ((values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
        ?.hardwareList as DynamicSupplyHardware[]) || [];
    const selectedId = values?.hardware;
    const selected = hardwareList.find((item) => item.hardwareId === selectedId);

    const processorLabel = selected?.hardwareName || "-";
    const locationLabel = selected?.location || "-";
    const priceNumber = typeof selected?.price === "number" ? selected.price : NaN;
    const hourlyPrice =
      isNaN(priceNumber) || priceNumber <= 0
        ? "-"
        : `$${priceNumber.toLocaleString(undefined, { maximumFractionDigits: 2 })}/hr`;
    const gpusPerHardware = values?.hardwareQuantity ?? "-";
    const durationQty = values?.durationQuantity ?? 1;
    const durationType =
      values?.durationType ?? (values.clusterType === ClusterType.KUBERNETES ? "daily" : "hourly");
    const hours = getClusterHourCount(durationQty, durationType) ?? durationQty;
    const totalCostNumber = !isNaN(priceNumber) && priceNumber > 0 ? priceNumber * hours : NaN;

    return {
      processorLabel,
      locationLabel,
      hourlyPrice,
      gpusPerHardware,
      totalCostNumber,
      priceNumber,
      locationId: selected?.location,
      supplier: selected?.supplier
    };
  }, [values]);

  const featuredDeepLinkHandledRef = useRef<string | null>(null);

  const applyHardwareToForm = useCallback(
    (hardware: DynamicSupplyHardware) => {
      setSelectedHardware(hardware);
      const defaultQty = Math.max(1, hardware?.numCards ?? 1);
      setFieldValue("hardware", hardware.hardwareId);
      setFieldValue(`hardwareQty.${hardware.hardwareId}`, defaultQty);
      setFieldValue("hardwareQuantity", defaultQty);
      setFieldValue("deploymentId", hardware.deploymentId || hardware.hardwareId);
      setFieldValue("locations", [`${hardware.location}`]);
      setFieldValue("location", hardware.location);
      setFieldValue("supplier", hardware.supplier);
      setFieldValue("isConfidentialCompute", hardware.isConfidentialCompute);
      setFieldValue("gpusPerNode", defaultQty.toString());
    },
    [setFieldValue]
  );

  useEffect(() => {
    (async () => {
      const { hardware_id } = queryParams;
      const savedPersistedState = getPersistedClusterState(persistedStorageKey);
      const canRestoreDraft =
        Boolean(savedPersistedState) && hasClusterDraftSession(persistedStorageKey);
      const isFeaturedHardwareDeepLink = Boolean(hardware_id && !canRestoreDraft);

      if (isFeaturedHardwareDeepLink) {
        if (!featuredHardwareDeepLinkConfigReady) {
          return;
        }

        if (featuredDeepLinkHandledRef.current === hardware_id) {
          return;
        }
        featuredDeepLinkHandledRef.current = hardware_id;

        setLoadingClusterData(true);
        try {
          const { hardware } = await fetchHardwareWithFilters();
          const match = hardware.find((h) => h.hardwareId === hardware_id);

          setFieldValue("createClusterData", {
            ...(values.createClusterData as Partial<CreateClusterData>),
            hardwareList: hardware
          });

          if (match && !match.isSoldOut) {
            applyHardwareToForm(match);

            if (advanceStepOnFeaturedHardwareDeepLink) {
              trackCompletedStep(processorStepTitle);
              trackProcessorSelected({
                processorId: match.hardwareId,
                processorName: match.hardwareName,
                processorType: (values.hardwareType || "gpu").toString().toUpperCase()
              });
              setShowSummaryPanel(true);
              setStepState(onRetrievePersistedState({ hardware: hardware_id }));
            }

            if (env.FEATUREFLAG_DELAYED_LOGIN && !isLoggedIn) {
              persistClusterDraft(persistedStorageKey, {
                ...objectWithkeys({ ...values, hardware: match.hardwareId }, PERSISTED_FIELDS),
                expires: getPersistedExpiration()
              });
            }
          }
        } catch (e) {
          console.error(e);
          featuredDeepLinkHandledRef.current = null;
        } finally {
          setLoadingClusterData(false);
        }
        return;
      }

      if (!env.FEATUREFLAG_DELAYED_LOGIN) {
        return;
      }

      // Draft without this tab's session flag (e.g. leave-and-return, or another tab):
      // skip restore. Do not delete localStorage here — another tab may still own the draft.
      if (!canRestoreDraft || !savedPersistedState) {
        return;
      }

      setLoadingClusterData(true);

      let persistedState = { ...savedPersistedState };

      if (hardware_id) {
        persistedState = {
          ...persistedState,
          hardware: hardware_id
        };
      }

      try {
        const { hardware } = await fetchHardwareWithFilters();

        if (hardware_id) {
          const match = hardware.find((h) => h.hardwareId === hardware_id);
          if (match && match.isSoldOut === false) {
            setFieldValue("hardwareQuantity", match.numCards);
          }
        }

        setFieldValue("createClusterData", {
          ...(values.createClusterData as Partial<CreateClusterData>),
          hardwareList: hardware
        });

        if (persistedState?.hardware) {
          const match = hardware.find((h) => h.hardwareId === persistedState.hardware);
          if (!match) {
            const firstNonSoldOutHardware = hardware.find((h) => h.isSoldOut === false);
            if (firstNonSoldOutHardware) {
              persistedState.hardware = firstNonSoldOutHardware.hardwareId;
              setFieldValue("hardwareQuantity", firstNonSoldOutHardware.numCards);
            }
          } else if (match && match.isSoldOut === true) {
            delete persistedState.hardware;
          }
        }

        const hasPersistedState = Object.keys(persistedState).length > 0;
        if (hasPersistedState) {
          for (const field of PERSISTED_FIELDS) {
            const value = persistedState[field];
            if (!value) continue;
            setFieldValue(field, value);
          }
        }

        setShowSummaryPanel(true);
        setStepState(onRetrievePersistedState(persistedState));
        // Keep draft + session so refresh while staying on create still restores;
        // SPA navigate-away and pay/deploy clear them.
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingClusterData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    fetchHardwareWithFilters,
    queryParams.hardware_id,
    applyHardwareToForm,
    advanceStepOnFeaturedHardwareDeepLink,
    featuredHardwareDeepLinkConfigReady
  ]);

  const setCurrentStep = (step: GenericFormStepType) => {
    setStepState((prev) => {
      const newState = { ...prev, [step]: "current" as const };
      // Reset all steps that come after `step` back to "next"
      const visited = new Set<string>([step]);
      let cursor = getNextStep(step);
      while (!visited.has(cursor)) {
        visited.add(cursor);
        newState[cursor] = "next";
        cursor = getNextStep(cursor);
      }
      return newState;
    });
  };

  const handleCompleteStep = (currentStep: GenericFormStepType) => {
    updateStepState(currentStep, "completed");
    const nextStep = getNextStep(currentStep, stepState);
    setCurrentStep(nextStep);
  };

  const handlePreviousStep = (currentStep: GenericFormStepType) => {
    const previousStep = getPreviousStep(currentStep, stepState);
    setCurrentStep(previousStep);
  };

  const updateStepState = (step: GenericFormStepType, state: "current" | "next" | "completed") => {
    setStepState((prev) => ({ ...prev, [step]: state }));
  };

  const onSelectHardware = applyHardwareToForm;

  const onNextProcessorStep = (hardwareOverride?: DynamicSupplyHardware | null) => {
    const hardware = hardwareOverride ?? selectedHardware;
    if (!hardware) return;
    trackCompletedStep(processorStepTitle);
    trackProcessorSelected({
      processorId: hardware.hardwareId,
      processorName: hardware.hardwareName,
      processorType: (values.hardwareType || "gpu").toString().toUpperCase()
    });
    handleCompleteStep("processor" as GenericFormStepType);
    const selectedIdStr = hardware.hardwareId;
    setShowSummaryPanel(true);
    if (env.FEATUREFLAG_DELAYED_LOGIN && !isLoggedIn) {
      persistClusterDraft(persistedStorageKey, {
        ...objectWithkeys({ ...values, hardware: selectedIdStr }, PERSISTED_FIELDS),
        expires: getPersistedExpiration()
      });
    }
  };

  const applyValidationErrors = (validationErrors: Record<string, unknown>) => {
    const invalidFields = invalidFieldKeysFromFormikErrors(validationErrors);
    trackEvent(AnalyticsEventType.CLUSTER_PAY_AND_DEPLOY_VALIDATION_BLOCKED, {
      clusterType: values.clusterType,
      invalidFields,
      currentStep: String(currentStep)
    });

    if (values.clusterType === ClusterType.VMAAS) {
      const { githubIds, name, vmImageType, networkServices } = validationErrors;
      // Always surface SSH key errors when pay/deploy is blocked — field validators may
      // not have been touched yet, and empty keys also fail isSubmitPossible.
      setTouched({
        sshKeys: (values.sshKeys ?? []).map(() => ({ key: true, value: true })),
        ...(name || !values.name ? { name: true } : {}),
        ...(githubIds ? { githubIds: true } : {}),
        ...(vmImageType ? { vmImageType: true } : {}),
        ...(networkServices ? { networkServices: true } : {})
      });
    } else if (values.clusterType === ClusterType.CONTAINER) {
      const {
        containerImage,
        username,
        secret,
        trafficPort,
        command,
        healthCheckPath,
        healthCheckIntervalSeconds,
        healthCheckTimeoutSeconds,
        healthCheckGracePeriodSeconds
      } = validationErrors;
      const touched = {
        containerImage: false,
        username: false,
        secret: false,
        trafficPort: false,
        command: false,
        healthCheckPath: false,
        healthCheckIntervalSeconds: false,
        healthCheckTimeoutSeconds: false,
        healthCheckGracePeriodSeconds: false
      };
      if (containerImage) {
        touched.containerImage = true;
      }
      if (values.isPrivate && username) {
        touched.username = true;
      }
      if (values.isPrivate && secret) {
        touched.secret = true;
      }
      if (trafficPort) {
        touched.trafficPort = true;
      }
      if (command) {
        touched.command = true;
      }
      if (healthCheckPath) {
        touched.healthCheckPath = true;
      }
      if (healthCheckIntervalSeconds) {
        touched.healthCheckIntervalSeconds = true;
      }
      if (healthCheckTimeoutSeconds) {
        touched.healthCheckTimeoutSeconds = true;
      }
      if (healthCheckGracePeriodSeconds) {
        touched.healthCheckGracePeriodSeconds = true;
      }
      setTouched(touched);
    }

    setTimeout(() => {
      const errorElement = document.querySelector(
        [
          ".border-red-500",
          ".text-red-500",
          ".border-red-dark-300",
          ".text-red-dark-300",
          "[aria-invalid='true']"
        ].join(", ")
      );
      const fallback =
        document.getElementById("ssh-keys-configuration") ||
        document.getElementById("hardware-grid-container");
      (errorElement || fallback)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const completeIntermediateStep = (step: GenericFormStepType) => {
    handleCompleteStep(step);
    trackIntermediateStepCompleted(step, values);

    if (env.FEATUREFLAG_DELAYED_LOGIN) {
      persistClusterDraft(persistedStorageKey, {
        ...objectWithkeys({ ...values }, PERSISTED_FIELDS),
        expires: getPersistedExpiration()
      });
    }
  };

  const handleSummaryPanelAction = async () => {
    if (currentStep === "processor" && !isLg) {
      onNextProcessorStep();
      return;
    }
    if (currentStep === "recipes" && !isLg) {
      handleCompleteStep("recipes" as GenericFormStepType);
      return;
    }

    const errors = (await validateForm()) as Record<string, unknown>;
    const hasFormikErrors = invalidFieldKeysFromFormikErrors(errors).length > 0;
    const canSubmit =
      !hasFormikErrors &&
      Boolean(values.name && values.hardware && values.hardwareQuantity) &&
      isSubmitPossible(currentStep as GenericFormStepType, values, errors);

    if (canSubmit) {
      if (isIntermediateStep(currentStep)) {
        completeIntermediateStep(currentStep as GenericFormStepType);
        return;
      }

      const hardwareList =
        ((values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
          ?.hardwareList as DynamicSupplyHardware[]) || [];
      const selected = hardwareList.find((h) => h.hardwareId === values.hardware);
      trackSummaryEvents(values, { processorName: selected?.hardwareName });

      if (env.FEATUREFLAG_DELAYED_LOGIN && !isLoggedIn) {
        persistClusterDraft(persistedStorageKey, {
          ...objectWithkeys(values, PERSISTED_FIELDS),
          expires: getPersistedExpiration()
        });
        setRequiresLoginMode("createCluster");
        setRequiresLoginModalOpen(true);
        return;
      }
      sendDripEvent("CLUSTER_DEPLOY_CLICKED");
      setIsPaymentDialogOpen(true);
    } else {
      applyValidationErrors(errors);
    }
  };

  /**
   * Apply a named saved deployment config: populate Formik fields, rematch hardware
   * (without silently swapping to a different GPU), advance steps when complete, and
   * sync the session draft so refresh keeps the loaded config.
   */
  const applySavedConfig = useCallback(
    async (
      config: SavedDeploymentConfig,
      options?: {
        /** Container recipes-first: called before apply when payload has no recipe */
        onCustomContainerConfig?: () => void;
        /** Override step state after apply (e.g. container with recipes) */
        buildStepState?: (
          hardwareMatched: boolean,
          config: SavedDeploymentConfig
        ) => Record<GenericFormStepType, "current" | "next" | "completed">;
      }
    ): Promise<{ hardwareMatch: MatchSavedHardwareResult }> => {
      if (config.kind === "container" && !config.payload.containerRecipe) {
        options?.onCustomContainerConfig?.();
      }

      let hardwareList =
        ((values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
          ?.hardwareList as DynamicSupplyHardware[]) || [];

      if (hardwareList.length === 0) {
        try {
          const result = await fetchHardwareWithFilters();
          hardwareList = result.hardware;
          setFieldValue("createClusterData", {
            ...(values.createClusterData as Partial<CreateClusterData>),
            hardwareList
          });
        } catch (e) {
          console.error(e);
        }
      }

      const hardwareMatch = matchSavedHardware(config.payload, hardwareList);

      // Set before applying values so useFormikAutoFieldName does not overwrite saved name
      if (config.payload.name) {
        setHasUserSpecifiedName(true);
      }

      const nextValues = applySavedPayload(values, config.payload);

      // Clear hardware fields when no usable match — do not silently pick another GPU
      if (hardwareMatch.status !== "matched") {
        nextValues.hardware = "";
        nextValues.hardwareQuantity = undefined;
        delete (nextValues as Record<string, unknown>).deploymentId;
        setSelectedHardware(null);
      }

      await setValues(nextValues);

      if (hardwareMatch.status === "matched") {
        applyHardwareToForm(hardwareMatch.hardware);
        // Re-apply payload fields that applyHardwareToForm may overwrite (e.g. qty)
        if (config.payload.hardwareQuantity != null) {
          setFieldValue("hardwareQuantity", config.payload.hardwareQuantity);
          setFieldValue(
            `hardwareQty.${hardwareMatch.hardware.hardwareId}`,
            config.payload.hardwareQuantity
          );
        }
        if (config.payload.gpusPerContainer != null) {
          setFieldValue("gpusPerContainer", config.payload.gpusPerContainer);
        }
        // Re-assert saved name after hardware apply (auto-name may race)
        if (config.payload.name) {
          setFieldValue("name", config.payload.name);
        }
      }

      const hardwareMatched = hardwareMatch.status === "matched";
      if (options?.buildStepState) {
        setStepState(options.buildStepState(hardwareMatched, config));
      } else if (hardwareMatched) {
        // Default: mark all known steps completed except the last as current
        const steps = Object.keys(defaultStepState) as GenericFormStepType[];
        const nextStepState = { ...defaultStepState };
        steps.forEach((step, index) => {
          nextStepState[step] = index === steps.length - 1 ? "current" : "completed";
        });
        setStepState(nextStepState);
      } else {
        // Stay / return to processor so user can pick hardware
        const steps = Object.keys(defaultStepState) as GenericFormStepType[];
        const nextStepState = { ...defaultStepState };
        steps.forEach((step) => {
          nextStepState[step] = step === "processor" ? "current" : "next";
        });
        if ("recipes" in nextStepState && config.payload.containerRecipe) {
          (nextStepState as Record<string, string>).recipes = "completed";
        }
        setStepState(nextStepState);
      }

      setShowSummaryPanel(hardwareMatched);

      persistClusterDraft(persistedStorageKey, {
        ...objectWithkeys(
          {
            ...nextValues,
            ...(hardwareMatched && hardwareMatch.status === "matched"
              ? { hardware: hardwareMatch.hardware.hardwareId }
              : {})
          },
          PERSISTED_FIELDS
        ),
        expires: getPersistedExpiration()
      });

      trackEvent(AnalyticsEventType.SAVED_DEPLOYMENT_CONFIG_LOADED, {
        kind: config.kind,
        schemaVersion: config.schemaVersion,
        hardwareMatch: hardwareMatch.status
      });

      return { hardwareMatch };
    },
    [
      values,
      fetchHardwareWithFilters,
      setFieldValue,
      setValues,
      applyHardwareToForm,
      defaultStepState,
      persistedStorageKey
    ]
  );

  return {
    values,
    errors,
    loadingClusterData,
    selectedHardware,
    showSummaryPanel,
    setShowSummaryPanel,
    isPaymentDialogOpen,
    setIsPaymentDialogOpen,
    stepState,
    setStepState,
    hasUserSpecifiedName,
    setHasUserSpecifiedName,
    isSubmitDisabled,
    clusterOptions,
    computedValues,
    setCurrentStep,
    updateStepState,
    onCompleteStep: handleCompleteStep,
    goToPreviousStep: handlePreviousStep,
    onSelectHardware,
    onNextProcessorStep,
    currentStep,
    handleSummaryPanelAction,
    setFieldValue,
    applySavedConfig
  };
};
